import type { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import { shouldShowScheduleVisitorDisplayName } from "../should-show-visitor-schedule-icon.function";

function readStringField(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === "string" ? value : undefined;
}

function getExternalInformationDisplayName(schedule: BookingScheduleInterface): string {
  const externalInfo = (schedule as BookingScheduleInterface & { externalInformation?: unknown })
    .externalInformation;
  if (!externalInfo || typeof externalInfo !== "object") {
    return "";
  }

  const info = externalInfo as Record<string, unknown>;
  const firstName =
    readStringField(info, "firstName") ??
    readStringField(info, "firstname") ??
    readStringField(info, "givenName");
  const surname =
    readStringField(info, "surname") ??
    readStringField(info, "lastName") ??
    readStringField(info, "lastname") ??
    readStringField(info, "familyName");
  const fromParts = `${firstName ?? ""} ${surname ?? ""}`.trim();
  if (fromParts) {
    return fromParts;
  }

  const fullName =
    readStringField(info, "name") ??
    readStringField(info, "fullName") ??
    readStringField(info, "displayName");
  return fullName?.trim() ?? "";
}

/** Visitor name for calendar and popover labels (visit-only and bookPlace workplace bookings). */
export function getScheduleVisitorDisplayName(
  schedule: Pick<BookingScheduleInterface, "visitorBookings" | "externalName">
): string {
  const visitor = schedule.visitorBookings?.[0];
  if (visitor) {
    const fromRegistration = `${visitor.firstName ?? ""} ${visitor.surname ?? ""}`.trim();
    if (fromRegistration) {
      return fromRegistration;
    }
  }

  return schedule.externalName?.trim() ?? "";
}

export function isScheduleBookedForExternalMember(schedule: BookingScheduleInterface): boolean {
  if (
    schedule.external === true ||
    schedule.externalBooking === true ||
    schedule.isGuest === true
  ) {
    return true;
  }

  if (shouldShowScheduleVisitorDisplayName(schedule)) {
    return false;
  }

  return Boolean(schedule.externalName?.trim() || schedule.externalEmail?.trim());
}

/** First and surname (or combined external name) for legacy team externals and other external rows. */
export function getScheduleExternalMemberDisplayName(schedule: BookingScheduleInterface): string {
  const fromVisitorRegistration = getScheduleVisitorDisplayName(schedule);
  if (fromVisitorRegistration) {
    return fromVisitorRegistration;
  }

  const fromExternalInformation = getExternalInformationDisplayName(schedule);
  if (fromExternalInformation) {
    return fromExternalInformation;
  }

  return schedule.externalName?.trim() ?? "";
}

export function resolveScheduleVisitorCalendarLabel(
  schedule: BookingScheduleInterface
): string | null {
  if (!shouldShowScheduleVisitorDisplayName(schedule)) {
    return null;
  }

  const visitorName = getScheduleVisitorDisplayName(schedule);
  return visitorName.length > 0 ? visitorName : null;
}

export function resolveScheduleExternalMemberCalendarLabel(
  schedule: BookingScheduleInterface
): string | null {
  if (!isScheduleBookedForExternalMember(schedule)) {
    return null;
  }

  const externalMemberName = getScheduleExternalMemberDisplayName(schedule);
  return externalMemberName.length > 0 ? externalMemberName : null;
}
